// Cached WebAuthn credential — the key to single-Face-ID payments.
//
// After a register/login ceremony we keep the credential's PUBLIC values (id +
// public key) in this browser. A returning payer can then rebuild their smart
// account WITHOUT a login ceremony (adapter.connectWithCredential), so the only
// biometric prompt left is the transaction signature itself — one Face ID.
//
// SAFETY: nothing here is secret. The public key and credential id are public by
// design; the PRIVATE key never leaves the Secure Enclave, and no payment can be
// made without a fresh signing biometric. If this cache is missing (cleared, or
// a browser that hasn't logged in yet) we simply fall back to the login
// ceremony, which re-caches. Per-browser (localStorage), like the device flag.

import { switchWallet } from "./walletSwitch";

const KEY = "m.cred";

export interface StoredCredential {
  id: string;
  publicKey: string;
  rpId?: string;
  address?: string;
  /** 1.5.44 — the passkey's user handle (base64url, public). */
  userHandle?: string;
}

export function readCredential(): StoredCredential | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as StoredCredential;
    return c && c.id && c.publicKey ? c : null;
  } catch {
    return null;
  }
}

/**
 * v1.1.6 — saving binds this browser to the credential's wallet. If it was
 * bound to a different wallet, the old one's per-wallet state is put aside
 * first (walletSwitch.ts). Returns true when the wallet changed.
 */
export function saveCredential(c: StoredCredential, opts: { ask?: boolean } = {}): boolean {
  let switched = false;
  const prev = readCredential();
  if (prev?.address && c.address && prev.address.toLowerCase() !== c.address.toLowerCase()) {
    switchWallet(prev.address, c.address, prev, opts.ask ?? true);
    switched = true;
  }
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        id: c.id,
        publicKey: c.publicKey,
        rpId: c.rpId,
        address: c.address,
        // 1.5.44 — a save without the handle (a silent rebuild, a hand-off) keeps the one known for this passkey.
        userHandle: c.userHandle ?? (prev?.id === c.id ? prev.userHandle : undefined),
      }),
    );
  } catch {
    /* ignore — optimization only; login fallback covers a miss */
  }
  return switched;
}

export function clearCredential(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
